import { useEffect, useMemo, useState } from 'react';
import { Tooltip } from '@odo/shared-ui';
import type { TaskBodyProps } from '../index';
import { advanceWorkflow, type OutgoingAction } from '../../../../services/workQueue';
import { TaskSidebarPanel } from '../TaskSidebarPanel';
import { buildSidebarTabs, IN_TREATMENT_TABS } from '../taskSidebarTabs';
import { TriggerNotesCard, type TriggerKind } from '../TriggerNotesCard';
import { useReviewActionRunner } from '../useReviewActionRunner';
import { GO_AHEAD_CHOICES, goAheadPayload, type GoAheadChoice } from './goAheadChoices';
import { GoAheadReasonModal } from './GoAheadReasonModal';

const BUTTON_CLASS =
    'px-4 py-2 rounded-md text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const PRIMARY_CLASS = `${BUTTON_CLASS} bg-gray-800 text-white hover:bg-gray-900`;
const SECONDARY_CLASS = `${BUTTON_CLASS} border border-gray-800 text-gray-800 bg-white hover:bg-gray-100`;
const NOT_AT_STEP_HINT = 'Not available at this step';

interface ResolvedChoice {
    choice: GoAheadChoice;
    action: OutgoingAction | undefined;
}

function OnBehalfBanner() {
    return (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-xs font-semibold text-amber-800 flex-shrink-0">
            Waiting on the orthodontist — act on their behalf
        </div>
    );
}

interface GoAheadFooterProps {
    choices: ResolvedChoice[];
    pending: string | null;
    disabled: boolean;
    onSelect: (resolved: ResolvedChoice) => void;
}

function GoAheadFooter({ choices, pending, disabled, onSelect }: GoAheadFooterProps) {
    return (
        <div className="flex-shrink-0 border-t border-gray-200 px-4 py-3 flex items-center justify-end gap-2">
            {choices.map((resolved) => {
                const { choice, action } = resolved;
                const button = (
                    <button
                        type="button"
                        onClick={() => onSelect(resolved)}
                        disabled={disabled || !action}
                        className={choice.consequence === null ? PRIMARY_CLASS : SECONDARY_CLASS}
                    >
                        {pending === choice.action ? 'Submitting…' : choice.label}
                    </button>
                );
                return action ? (
                    <span key={choice.action} className="inline-block">
                        {button}
                    </span>
                ) : (
                    <Tooltip key={choice.action} content={NOT_AT_STEP_HINT} side="top">
                        <span className="inline-block">{button}</span>
                    </Tooltip>
                );
            })}
        </div>
    );
}

// Ref-1 / Hold-3 and Ret-1 / Hold-4 on V2 cases: ODO decides the go-ahead for the orthodontist.
function GoAheadTask({
    workCase: c,
    statusMeta,
    onPayloadChange,
    onTaskComplete,
    kind
}: TaskBodyProps & { kind: TriggerKind }) {
    const { progress, runAction } = useReviewActionRunner(c.id, onTaskComplete, undefined);
    const [reasonFor, setReasonFor] = useState<ResolvedChoice | null>(null);

    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: false, bodyOwnsSubmit: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const infoTabs = useMemo(
        () => buildSidebarTabs(c.id, IN_TREATMENT_TABS).map((t) => ({ ...t, trailing: true })),
        [c.id]
    );
    const tabs = [
        {
            key: 'odo-notes',
            label: 'ODO notes',
            content: (
                <div className="flex-1 min-h-0 p-3 flex flex-col">
                    <TriggerNotesCard patientId={c.id} kind={kind} />
                </div>
            )
        },
        ...infoTabs
    ];
    const choices = GO_AHEAD_CHOICES[kind].map((choice) => ({
        choice,
        action: statusMeta.outgoingActions.find((a) => a.action === choice.action)
    }));

    const submit = ({ choice, action }: ResolvedChoice, reason: string): void => {
        if (!action) return;
        void runAction(
            action,
            () => advanceWorkflow(c.id, action.action, goAheadPayload(choice, reason)),
            `GoAheadTask.${choice.action}`,
            `Failed to ${choice.label.toLowerCase()}. Please try again.`
        );
    };
    const select = (resolved: ResolvedChoice): void => {
        if (resolved.choice.consequence === null) submit(resolved, '');
        else setReasonFor(resolved);
    };

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <OnBehalfBanner />
            <TaskSidebarPanel tabs={tabs} keepMounted flush />
            <GoAheadFooter
                choices={choices}
                pending={progress.pending}
                disabled={progress.pending !== null || progress.succeeded !== null}
                onSelect={select}
            />
            {reasonFor && (
                <GoAheadReasonModal
                    workCase={c}
                    choice={reasonFor.choice}
                    submitting={progress.pending === reasonFor.choice.action}
                    onCancel={() => setReasonFor(null)}
                    onConfirm={(reason) => submit(reasonFor, reason)}
                />
            )}
        </div>
    );
}

export function RefinementGoAheadTask(props: TaskBodyProps) {
    return <GoAheadTask {...props} kind="refinement" />;
}

export function RetentionGoAheadTask(props: TaskBodyProps) {
    return <GoAheadTask {...props} kind="retention" />;
}
